import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UploadIcon from '@mui/icons-material/Upload';
import { Accordion, AccordionDetails, AccordionSummary, Button, Card, CardMedia, CircularProgress, Container, IconButton, SelectChangeEvent, Stack, Typography } from '@mui/material';
import * as lodash from 'lodash';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import Swal from 'sweetalert2';
import * as EngineHelper from '../../api/engine/engineHelper';
import { ELanguages, TLanguages, languages } from '../../localization';
import { route } from '../../route/Index';
import InputText from '../../shared/component/InputText';
import isAdmin from '../../shared/hoc/isAdmin';
import { IEngine, ITranslation } from '../../shared/iProject';
import variable from '../../shared/style/variable';

interface IEngineDetailProps {}

const EngineContainer = styled(Container)`
    padding: 0;
    flex-grow: 1;
`;

let translations: ITranslation[] = [];

const EngineDetail = (props: IEngineDetailProps) => {
    let { param } = useParams();

    const fileInputRef = React.useRef<HTMLInputElement>(null);

    const navigate = useNavigate();

    const { t } = useTranslation();

    const [isLoading, setIsLoading] = React.useState(false);
    const [engine, setEngine] = React.useState<IEngine | null>();
    const [image, setImage] = React.useState('');

    React.useEffect(() => {
        const fetchEngine = async () => {
            setIsLoading(true);
            let engineId = parseInt(param!);
            let initialEngine = await EngineHelper.getEngineByIdAsync(engineId);
            setEngine(initialEngine.data);
            translations = lodash.cloneDeep(initialEngine.data.translations!);
            setImage(initialEngine.data.image!.imageUrl);
            setIsLoading(false);
        };
        fetchEngine();
    }, []);

    const handleClickSave = async () => {
        let payload = {
            typeOfPropeller: engine!.typeOfPropeller,
            propellerDirection: engine!.propellerDirectionWithRespectToTheQuay,
            numberOfPropellers: engine!.numberOfPropellers,
            translations,
        };
        const formData = new FormData();
        formData.append('updateEngineRequest', JSON.stringify(payload));
        formData.append('imageFile', image);
        let res = await EngineHelper.updateSelectedEnginesWithImageAndTranslationAsync(formData);
        if (res.status == 200) {
            Swal.fire({
                title: t('savedEngine'),
                icon: 'success',
                toast: true,
                position: 'top',
                showConfirmButton: false,
                timer: 2000,
            });
        }
    };

    const handleChangeTraslation = (event: React.ChangeEvent<any> | SelectChangeEvent<any>) => {
        let { name, value } = event.target;
        let index = translations.findIndex(x => x.iso === name);
        if (index !== -1) {
            translations[index].name = value;
        } else {
            translations.push({
                iso: name,
                name: value,
            });
        }
    };

    const handleClickRemoveTranslation = (iso: string) => (translations = translations.filter(x => x.iso !== iso));

    const handleImageChange = (event: React.ChangeEvent<any> | SelectChangeEvent<any>) => {
        const file = event.target.files[0];
        setImage(file);
    };

    return (
        <>
            <Stack direction={'row'} padding={'0 1rem'} alignItems={'center'}>
                <ArrowForwardIosIcon style={{ fill: variable.greenLight }} />
                <Typography variant='h6' padding={'1rem 0'} color={variable.greenLight}>
                    {param == 'newEngine' ? t('addNewEngineType') : t('updateEngineType')}
                </Typography>
            </Stack>
            {isLoading && <CircularProgress />}
            {!isLoading && engine && (
                <EngineContainer maxWidth={'md'}>
                    <Stack height={'95%'} direction='column'>
                        <Stack height={'100%'} direction={'row'} spacing={1}>
                            <Stack width={'33%'} position='relative'>
                                <Stack variant='scrollable'>
                                    {Object.keys(languages).map(x => (
                                        <Accordion disableGutters>
                                            <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls='panel1a-content' id='panel1a-header'>
                                                <Typography>{languages[x as TLanguages]}</Typography>
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                <Stack direction={'column'} spacing={2}>
                                                    <InputText
                                                        defaultValue={engine!.translations!.find(y => y.iso == x)?.name}
                                                        id={x}
                                                        inputLabel={t('name')}
                                                        name={x}
                                                        customOnChange={handleChangeTraslation}
                                                    />
                                                    {x !== ELanguages.en && (
                                                        <Stack alignItems={'end'}>
                                                            <IconButton aria-label={`delete-engine-${x}`} onClick={() => handleClickRemoveTranslation(x)}>
                                                                <DeleteIcon />
                                                            </IconButton>
                                                        </Stack>
                                                    )}
                                                </Stack>
                                            </AccordionDetails>
                                        </Accordion>
                                    ))}
                                </Stack>
                            </Stack>
                            <Stack width={'33%'}>
                                <Accordion disableGutters>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls='panel1a-content' id='panel1a-header'>
                                        <Typography>{t('pictures')}</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Stack direction={'column'} spacing={2}>
                                            {image ? (
                                                <>
                                                    <Card variant='standard'>
                                                        <CardMedia height={160} component='img' image={typeof image === 'string' ? image : URL.createObjectURL(image)} alt='image-engine' />
                                                    </Card>
                                                    <IconButton style={{ justifyContent: 'end' }} aria-label='remove-image-engine' onClick={() => setImage('')}>
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </>
                                            ) : (
                                                <Stack height={160} justifyContent={'center'} style={{ border: `0.1rem solid ${variable.greyLight}` }}>
                                                    <IconButton aria-label='add-image-engine' onClick={() => fileInputRef.current!.click()}>
                                                        <UploadIcon />
                                                    </IconButton>
                                                    <input onChange={handleImageChange} multiple={false} ref={fileInputRef} type='file' hidden />
                                                </Stack>
                                            )}
                                        </Stack>
                                    </AccordionDetails>
                                </Accordion>
                            </Stack>
                        </Stack>
                        <Stack alignItems='end'>
                            <Stack direction={'row'} spacing={2}>
                                <Button variant='white' onClick={() => navigate(route.backoffice)}>
                                    {t('back')}
                                </Button>
                                <Button variant='orange' onClick={handleClickSave}>
                                    {t('save')}
                                </Button>
                            </Stack>
                        </Stack>
                    </Stack>
                </EngineContainer>
            )}
        </>
    );
};

export default isAdmin(EngineDetail);
